import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, ChevronRight, ArrowRight } from "lucide-react";
import { DOCS_SECTIONS, DOCS_CONTENT } from "@/data/docs";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

const flatSlugs = DOCS_SECTIONS.flatMap((g) => g.topics.map((t) => t.slug));

const SidebarContent = ({ activeSlug, onPick }) => {
  return (
    <nav className="py-6 px-3" data-testid="docs-sidebar-nav">
      <div className="px-3 pb-4">
        <div className="tag-label text-[var(--pg-text-muted)]">
          /// Documentation
        </div>
        <div className="font-display font-bold text-xl mt-1">v1.1</div>
      </div>
      <div className="space-y-6">
        {DOCS_SECTIONS.map((g) => (
          <div key={g.group}>
            <div className="px-3 mb-2 tag-label text-[var(--pg-text-muted)]">
              {g.group}
            </div>
            <ul>
              {g.topics.map((t) => {
                const active = t.slug === activeSlug;
                return (
                  <li key={t.slug}>
                    <Link
                      to={`/docs/${t.slug}`}
                      data-testid={`docs-nav-${t.slug}`}
                      onClick={onPick}
                      className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors border-l-2 ${
                        active
                          ? "border-l-[var(--pg-cyan)] text-white bg-pg-surface"
                          : "border-l-transparent text-[var(--pg-text-dim)] hover:text-white hover:bg-pg-surface"
                      }`}
                    >
                      {active && (
                        <ChevronRight className="w-3 h-3 text-cyan-accent shrink-0" />
                      )}
                      <span className={active ? "" : "ml-5"}>{t.title}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
};

export const Docs = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const activeSlug = slug && DOCS_CONTENT[slug] ? slug : "getting-started";
  const content = DOCS_CONTENT[activeSlug];
  const [sheetOpen, setSheetOpen] = useState(false);

  const idx = flatSlugs.indexOf(activeSlug);
  const prevSlug = idx > 0 ? flatSlugs[idx - 1] : null;
  const nextSlug = idx < flatSlugs.length - 1 ? flatSlugs[idx + 1] : null;
  const findTitle = (s) =>
    DOCS_SECTIONS.flatMap((g) => g.topics).find((t) => t.slug === s)?.title;

  useDocumentMeta({
    title: `${content.title} — Poly Grid Docs`,
    description: content.intro,
    path: `/docs/${activeSlug}`,
  });

  return (
    <div className="relative min-h-screen bg-[var(--pg-bg)]">
      <Navbar />

      {/* Mobile: open drawer */}
      <div className="md:hidden sticky top-14 z-30 border-b border-pg bg-[var(--pg-bg)]/95 backdrop-blur">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="tag-label text-[var(--pg-text-dim)]">
            Docs / {content.title}
          </div>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                data-testid="docs-mobile-menu-trigger"
                className="rounded-sm border-pg bg-transparent tag-label"
              >
                <Menu className="w-4 h-4 mr-1.5" /> Topics
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="bg-[var(--pg-bg)] border-pg p-0 w-72"
              data-testid="docs-mobile-sheet"
            >
              <SidebarContent
                activeSlug={activeSlug}
                onPick={() => setSheetOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex gap-10">
          {/* Sticky sidebar */}
          <aside
            data-testid="docs-sidebar"
            className="hidden md:block w-64 lg:w-72 shrink-0 border-r border-pg"
          >
            <div className="sticky top-14 h-[calc(100vh-3.5rem)]">
              <ScrollArea className="h-full">
                <SidebarContent activeSlug={activeSlug} />
              </ScrollArea>
            </div>
          </aside>

          {/* Main content */}
          <main
            data-testid="docs-main-content"
            className="flex-1 min-w-0 py-12 lg:py-16"
          >
            <div className="max-w-3xl">
              <div className="tag-label text-cyan-accent">
                /// {content.eyebrow}
              </div>
              <h1 className="font-display mt-3 text-4xl lg:text-5xl font-black tracking-tighter uppercase">
                {content.title}
              </h1>
              <p className="mt-5 text-[17px] text-[var(--pg-text-dim)] leading-relaxed">
                {content.intro}
              </p>

              <div className="mt-12 space-y-12">
                {content.sections.map((s, i) => {
                  const anchor = s.h2.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <section key={i} id={anchor} className="scroll-mt-24">
                      <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight group">
                        <a
                          href={`#${anchor}`}
                          className="hover:text-cyan-accent transition-colors"
                          data-testid={`docs-anchor-${anchor}`}
                        >
                          <span className="text-cyan-accent mr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            #
                          </span>
                          {s.h2}
                        </a>
                      </h2>
                      <p className="mt-4 text-[15px] leading-relaxed text-[var(--pg-text-dim)]">
                        {s.body}
                      </p>
                      {s.code && (
                        <pre className="mt-5 bg-[#0a0a0a] border border-pg p-4 text-[13px] leading-relaxed font-mono text-[var(--pg-text-dim)] overflow-x-auto rounded-sm">
                          {s.code}
                        </pre>
                      )}
                    </section>
                  );
                })}
              </div>

              {/* Prev / Next */}
              <div className="mt-20 grid grid-cols-2 gap-4 pt-8 border-t border-pg">
                {prevSlug ? (
                  <button
                    data-testid="docs-prev-link"
                    onClick={() => navigate(`/docs/${prevSlug}`)}
                    className="text-left p-5 border border-pg hover:bg-pg-surface transition-colors group"
                  >
                    <div className="tag-label text-[var(--pg-text-muted)]">
                      ← Previous
                    </div>
                    <div className="mt-1.5 font-display font-bold group-hover:text-cyan-accent transition-colors">
                      {findTitle(prevSlug)}
                    </div>
                  </button>
                ) : (
                  <div />
                )}
                {nextSlug ? (
                  <button
                    data-testid="docs-next-link"
                    onClick={() => navigate(`/docs/${nextSlug}`)}
                    className="text-right p-5 border border-pg hover:bg-pg-surface transition-colors group"
                  >
                    <div className="tag-label text-[var(--pg-text-muted)]">
                      Next →
                    </div>
                    <div className="mt-1.5 font-display font-bold group-hover:text-cyan-accent transition-colors flex items-center justify-end gap-2">
                      {findTitle(nextSlug)}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </button>
                ) : (
                  <div />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Docs;
